import type { ReactNode } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { canEditData } from '@repo/shared/common';
import { ensureRouteData } from '~/lib/query';
import { currentOrganizationQueryOptions } from '~/lib/queries/organization';
import { resolveUploadPlanQueryOptions } from '~/features/upload/use-uploads';
import { UploadFlowPage } from '~/features/upload/UploadFlowPage';

/** Search params: an optional `resume` (a draft file id) to reopen into the wizard. */
interface UploadsNewSearch {
  readonly resume?: string;
}

export const Route = createFileRoute('/_protected/uploads/new')({
  // Viewer guard: starting a new upload is edit-only. Bounce viewers to
  // /unauthorized before the plan-resolve loader runs.
  beforeLoad: async ({ context }) => {
    const { role } = await context.queryClient.ensureQueryData(
      currentOrganizationQueryOptions(),
    );
    if (role === null || !canEditData(role)) {
      throw redirect({ to: '/unauthorized' });
    }
  },
  validateSearch: (search: Record<string, unknown>): UploadsNewSearch =>
    typeof search.resume === 'string' && search.resume.length > 0
      ? { resume: search.resume }
      : {},
  loaderDeps: ({ search }): { readonly resume: string | undefined } => ({
    resume: search.resume,
  }),
  loader: async ({ context, deps }): Promise<void> => {
    // Fresh entry: prefetch the session's plan resolve (the guide-vs-skip decision)
    // so the page arrives with it warm — no client-side "Preparing your upload"
    // round-trip. A resumed draft reads its own detail client-side and ignores the
    // plan, so there's nothing to prefetch then.
    if (deps.resume === undefined) {
      // Best-effort: the plan resolve is never a blocker. On the SSR/direct-load path
      // ensureRouteData AWAITS in-process, so an org-scoping/auth failure (e.g. an
      // org-less user) would otherwise throw → route error page. Swallow it: the
      // component's own ensureQueryData().catch then degrades to the guide with a
      // default plan (the documented contract). The client branch is a non-throwing
      // prefetch, so this try/catch only changes the SSR path.
      try {
        await ensureRouteData(context.queryClient, resolveUploadPlanQueryOptions());
      } catch {
        // Intentionally ignored — see above.
      }
    }
  },
  component: UploadsNewRoute,
});

function UploadsNewRoute(): ReactNode {
  const { resume } = Route.useSearch();
  return <UploadFlowPage resumeUploadId={resume} />;
}
